import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon } from 'lucide-react';
import { interiorPages } from '../../data/samples';
export function InteriorPreview() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [zoom, setZoom] = useState<number | null>(null);
  const updateEdges = () => {
    const t = trackRef.current;
    if (!t) return;
    const w = (t.children[0] as HTMLElement | undefined)?.offsetWidth ?? 1;
    setActive(Math.round(t.scrollLeft / (w + 8)));
    setAtStart(t.scrollLeft <= 2);
    setAtEnd(t.scrollLeft + t.clientWidth >= t.scrollWidth - 2);
  };
  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, []);
  useEffect(() => {
    if (zoom === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoom(null);
      if (e.key === 'ArrowRight') setZoom(z => z === null ? z : (z + 1) % interiorPages.length);
      if (e.key === 'ArrowLeft') setZoom(z => z === null ? z : (z - 1 + interiorPages.length) % interiorPages.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);
  const goTo = (i: number) => {
    const t = trackRef.current;
    if (!t) return;
    const idx = Math.max(0, Math.min(interiorPages.length - 1, i));
    const child = t.children[idx] as HTMLElement | undefined;
    if (child) t.scrollTo({
      left: child.offsetLeft - t.offsetLeft,
      behavior: 'smooth'
    });
  };
  const arrow = 'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-navy shadow-[0_2px_6px_rgba(16,24,32,0.3)] transition-[transform,opacity] duration-150 ease-out active:scale-95 disabled:pointer-events-none disabled:opacity-40';
  return <section className="bg-paper px-4 py-7" data-id="element-94" data-mp="399y3hemfsp2i">
      <div className="mx-auto max-w-[700px]" data-id="element-95" data-mp="398mzpeszrhbe">
        <div className="mx-auto max-w-[520px] text-center" data-id="element-96" data-mp="3e0o6wmbayhuk">
          <p className="font-display text-[9px] uppercase tracking-[0.25em] text-muted" data-id="element-97" data-mp="3e398da7ouvrs">Look inside</p>
          <h2 className="mt-0.5 font-display text-[21px] uppercase leading-[1.05] text-navy md:text-[24px]" data-id="element-98" data-mp="3j23r6c5nokbw">
            Discover what’s inside Diesel Diagnostic Pro™
          </h2>
          <p className="mt-2 text-[11.5px] italic leading-[1.55] text-muted" data-id="element-99" data-mp="2qbvj5d4u3gb1">
            Visual diagnostic workflows, testing sequences, fuel-system references, scan-data guides, turbo and boost diagnostics, aftertreatment
            fault paths, symptom-based decision trees and ready-to-use workshop tools.
          </p>
        </div>

        <div className="relative mt-5" data-id="element-100" data-mp="fjagc749i9lf">
          <div ref={trackRef} onScroll={updateEdges} className="flex snap-x snap-mandatory gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-101" data-mp="fi93py7llf1l">
            {interiorPages.map((p, i) => <button key={p.src} type="button" onClick={() => setZoom(i)} aria-label={`Enlarge interior page ${i + 1}`} className="group relative w-[88%] shrink-0 snap-start overflow-hidden rounded-[3px] bg-white shadow-[0_10px_24px_rgba(16,24,32,0.18)] ring-1 ring-black/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold md:w-[calc(50%-4px)]" data-id="element-102" data-mp="1qbia09huh3tj" data-mpi={i}>
                <img src={p.src} alt={p.alt} className="block h-auto w-full" loading={i < 2 ? 'eager' : 'lazy'} draggable={false} data-id="element-103" data-mp="17f0av70o21n9" />
                <span className="absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-navy/85 text-gold opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100" data-id="element-104" data-mp="31qouhmhuesdz">
                  <ZoomInIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-105" data-mp="34t2y72hd2bio" />
                </span>
              </button>)}
          </div>
          <button type="button" onClick={() => goTo(active - 1)} disabled={atStart} aria-label="Previous interior page" className={`${arrow} left-0 -translate-x-1/2`} data-id="element-106" data-mp="2uum4vyuy9ktg">
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-107" data-mp="2zvjn2bfymeuy" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} disabled={atEnd} aria-label="Next interior page" className={`${arrow} right-0 translate-x-1/2`} data-id="element-108" data-mp="2m0qyyom07qcr">
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-109" data-mp="2c4np0wkwunf9" />
          </button>
        </div>

        <div className="mt-3 flex justify-center gap-1.5" role="tablist" aria-label="Interior pages" data-id="element-110" data-mp="2h40ku6trf4tz">
          {interiorPages.map((_, i) => <button key={i} type="button" role="tab" aria-selected={active === i} aria-label={`Interior page ${i + 1}`} onClick={() => goTo(i)} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-4 bg-gold' : 'w-1.5 bg-line'}`} data-id="element-111" data-mp="235alslndqv2z" data-mpi={i} />)}
        </div>
        <p className="mt-2 text-center text-[9.5px] text-muted" data-id="element-112" data-mp="1sicbwk6x4rzj">Swipe to see more interior pages · Tap a page to enlarge</p>
      </div>

      <AnimatePresence data-id="element-113" data-mp="27ebj7sm7eo3m">
        {zoom !== null && <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/90 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => setZoom(null)} role="dialog" aria-modal="true" aria-label="Interior page preview" data-id="element-114" data-mp="22ga057os2os5">
            <motion.img key={zoom} src={interiorPages[zoom].src} alt={interiorPages[zoom].alt} className="max-h-[92vh] w-auto max-w-full rounded-[3px] shadow-[0_20px_50px_rgba(0,0,0,0.5)]" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-115" data-mp="2l82rkxo781xr" />
            <button type="button" onClick={() => setZoom(null)} aria-label="Close preview" className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-gold text-navy" data-id="element-116" data-mp="21kmkojwkpcls">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-117" data-mp="2ak9qdhwc54kn" />
            </button>
            <button type="button" aria-label="Previous page" onClick={e => {
          e.stopPropagation();
          setZoom((zoom - 1 + interiorPages.length) % interiorPages.length);
        }} className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-navy" data-id="element-118" data-mp="2fjcn8f25w4hj">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-119" data-mp="8v2fcnxlqmm7" />
            </button>
            <button type="button" aria-label="Next page" onClick={e => {
          e.stopPropagation();
          setZoom((zoom + 1) % interiorPages.length);
        }} className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-gold text-navy" data-id="element-120" data-mp="3vpvmc211vnu">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-121" data-mp="mmgyqvvf8pts" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}